import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { PageHead, Card } from "@/pages/app/AppShell";
import { CompanyAvatar } from "@/components/CompanyAvatar";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const LIST_FIELDS = ["locations", "previous_partnerships", "partnership_interests"];
const L = ({ label, children }) => <label className="block text-xs uppercase tracking-[0.14em] text-slate-400">{label}<div className="mt-2 normal-case tracking-normal">{children}</div></label>;

export default function ProfileEdit() {
  const { refresh } = useAuth();
  const [sp] = useSearchParams();
  const [c, setC] = useState(null);
  const [meta, setMeta] = useState({ company_types: [], industries: [], regions: [], budgets: [], countries: [] });
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/companies/me").then((r) => {
      const d = r.data || {};
      LIST_FIELDS.forEach((k) => { d[k] = (d[k] || []).join(", "); });
      d.socials = d.socials || {};
      setC(d);
    }).catch(() => {});
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    const body = { ...c };
    LIST_FIELDS.forEach((k) => { body[k] = String(c[k] || "").split(",").map((x) => x.trim()).filter(Boolean); });
    try {
      await api.put("/companies/me", body);
      toast.success("Profile saved");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const logo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/companies/me/logo", fd);
      setC({ ...c, logo_url: data.logo_url });
      toast.success("Logo updated");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!c) return <p className="text-slate-400">Loading…</p>;
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const setSoc = (k) => (e) => setC({ ...c, socials: { ...c.socials, [k]: e.target.value } });

  return (
    <div data-testid="profile-edit-page">
      {sp.get("welcome") && <div className="mb-6 rounded-2xl border border-[#E0218A]/40 bg-[#E0218A]/10 p-5 text-sm text-[#2A1240]" data-testid="profile-welcome">Welcome to Balqis Book! Complete your company profile so partners can find you and smart alerts can match you.</div>}
      <PageHead kicker="Your public company profile" title="Company profile">
        <Link to={`/companies/${c.id}`} className="btn-ghost !py-2.5" data-testid="profile-view-public"><ExternalLink className="h-4 w-4" />View public page</Link>
        <button className="btn-gold !py-2.5" onClick={save} disabled={busy} data-testid="profile-save-button">{busy ? "Saving…" : "Save profile"}</button>
      </PageHead>
      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="p-6 lg:col-span-4 h-fit">
          <div className="flex items-center gap-4">
            <CompanyAvatar c={c} size="h-20 w-20" text="text-3xl" />
            <div>
              <input type="file" accept="image/*" ref={ref} className="hidden" onChange={logo} data-testid="profile-logo-input" />
              <button className="btn-ghost !py-2 !px-4 !text-xs" onClick={() => ref.current?.click()} data-testid="profile-logo-upload"><Upload className="h-3.5 w-3.5" />Upload logo</button>
            </div>
          </div>
          <div className="mt-6 space-y-4">
            <L label="Company"><input className="field" value={c.name || ""} onChange={set("name")} data-testid="profile-name-input" /></L>
            <L label="Type"><select className="field" value={c.company_type || "Brand"} onChange={set("company_type")} data-testid="profile-type-select">{meta.company_types.map((t) => <option key={t}>{t}</option>)}</select></L>
            <L label="Industry"><select className="field" value={c.industry || ""} onChange={set("industry")} data-testid="profile-industry-select"><option value="">Choose</option>{meta.industries.map((t) => <option key={t}>{t}</option>)}</select></L>
            <L label="Region"><select className="field" value={c.region || ""} onChange={set("region")} data-testid="profile-region-select"><option value="">Choose</option>{meta.regions.map((t) => <option key={t}>{t}</option>)}</select></L>
            <L label="Country"><select className="field" value={c.country || ""} onChange={set("country")} data-testid="profile-country-select"><option value="">Choose</option>{(meta.countries || []).map((t) => <option key={t}>{t}</option>)}</select></L>
            <L label="Estimated budget range"><select className="field" value={c.budget_range || ""} onChange={set("budget_range")} data-testid="profile-budget-select"><option value="">Choose</option>{meta.budgets.map((t) => <option key={t}>{t}</option>)}</select></L>
          </div>
        </Card>
        <Card className="p-6 lg:col-span-8 space-y-5">
          <L label="About"><textarea className="field" rows={4} value={c.about || ""} onChange={set("about")} data-testid="profile-about-input" /></L>
          <div className="grid sm:grid-cols-2 gap-5">
            <L label="Website"><input className="field" value={c.website || ""} onChange={set("website")} data-testid="profile-website-input" /></L>
            <L label="Locations (comma separated)"><input className="field" value={c.locations} onChange={set("locations")} data-testid="profile-locations-input" /></L>
          </div>
          <L label="Target audience"><input className="field" value={c.target_audience || ""} onChange={set("target_audience")} data-testid="profile-audience-input" /></L>
          <L label="Partnership interests (comma separated — used for smart matching)"><input className="field" value={c.partnership_interests} onChange={set("partnership_interests")} placeholder="Beauty, Creators, Events, Middle East" data-testid="profile-interests-input" /></L>
          <L label="Previous partnerships (comma separated)"><input className="field" value={c.previous_partnerships} onChange={set("previous_partnerships")} data-testid="profile-previous-input" /></L>
          <div className="grid sm:grid-cols-3 gap-5">
            {["instagram", "linkedin", "x"].map((k) => <L key={k} label={k}><input className="field" value={c.socials[k] || ""} onChange={setSoc(k)} placeholder="https://" data-testid={`profile-social-${k}`} /></L>)}
          </div>
        </Card>
      </div>
    </div>
  );
}
